import {
  AI_GENERATE_DOCK_ONBOARDING_CONNECTOR_OVERLAP_PX,
  AI_GENERATE_DOCK_ONBOARDING_HOLE_OUTSET,
  AI_GENERATE_DOCK_ONBOARDING_STEPS,
  type AiGenerateDockOnboardingStep,
} from '../consts/aiGenerateDockOnboarding.const';

import { type AiGenerateDockGateKind } from './aiGenerateDockGate';

/**
 * 코치마크 진행 — 기다림(아직 안 띄움) → 단계 보임 → 끝. 끝은 다시 돌아오지 않는다(저장소가 영구로 기억한다).
 * 보이는 동안 입력창이 잠시 숨겨져도(채팅 패널 등) 단계는 그대로 두고, 다시 보이면 그 단계부터 잇는다.
 * 입력창을 ✕ 로 닫으면 끝이 아니라 기다림으로 돌아간다 — 다시 열면 1단계부터 다시 보인다.
 */
export type AiGenerateDockOnboardingState =
  | { phase: 'waiting' }
  | { phase: 'showing'; step: AiGenerateDockOnboardingStep }
  | { phase: 'ended' };

export type AiGenerateDockOnboardingAction =
  | { type: 'start' }
  | { type: 'next' }
  | { type: 'end' }
  /** 다음에 열 때 처음부터 다시 — 기억하지 않는 끝(✕ 닫기). */
  | { type: 'rewind' };

/** 끝난 이유 — 마지막 「시작하기」만 complete, 나머지는 사용자가 다른 일을 시작해 걷어 낸 것이다. */
export type AiGenerateDockOnboardingEndReason =
  | 'complete'
  | 'escape'
  | 'close'
  | 'submit'
  | 'conditions_open';

export const reduceAiGenerateDockOnboarding = (
  state: AiGenerateDockOnboardingState,
  action: AiGenerateDockOnboardingAction,
): AiGenerateDockOnboardingState => {
  switch (action.type) {
    case 'start':
      return state.phase === 'waiting'
        ? { phase: 'showing', step: AI_GENERATE_DOCK_ONBOARDING_STEPS[0] }
        : state;
    case 'next': {
      if (state.phase !== 'showing') return state;
      const next =
        AI_GENERATE_DOCK_ONBOARDING_STEPS[
          AI_GENERATE_DOCK_ONBOARDING_STEPS.indexOf(state.step) + 1
        ];
      return next ? { phase: 'showing', step: next } : { phase: 'ended' };
    }
    case 'end':
      return { phase: 'ended' };
    case 'rewind':
      return state.phase === 'ended' ? state : { phase: 'waiting' };
  }
};

/**
 * 이 이유로 끝났으면 다시 띄우지 않게 기억하나. ✕ 닫기는 안내를 끄려던 게 아니라 입력창을 닫은 것이라
 * 기억하지 않는다 — 다음에 열면 다시 보인다(횟수 제한 없음). 나머지는 끝까지 봤거나 직접 걷은 것이다.
 */
export const shouldRememberAiGenerateDockOnboarding = (
  reason: AiGenerateDockOnboardingEndReason,
) => reason !== 'close';

export const isLastAiGenerateDockOnboardingStep = (
  step: AiGenerateDockOnboardingStep,
) => step === AI_GENERATE_DOCK_ONBOARDING_STEPS.at(-1);

/**
 * 지금 코치마크를 보일 수 있나. 실행할 수 없는 사용자(게스트·무료 소진·채팅 응답 중)에겐 배너가 먼저고,
 * 입력창 위 안내가 떠 있으면 카드가 그 자리를 덮는다. 좁은 화면은 1차 범위 밖이다(키보드로 입력창이 움직인다).
 * 조건 메뉴를 먼저 열었으면 메뉴를 고르는 동안은 띄우지 않고, 닫히면 그때 띄운다.
 */
export const canShowAiGenerateDockOnboarding = ({
  open,
  visible,
  layout,
  gateKind,
  running,
  hasNotice,
  conditionsMenuOpen,
}: {
  open: boolean;
  visible: boolean;
  layout: 'wide' | 'narrow';
  gateKind: AiGenerateDockGateKind;
  running: boolean;
  hasNotice: boolean;
  conditionsMenuOpen: boolean;
}) =>
  open &&
  visible &&
  layout === 'wide' &&
  gateKind === 'open' &&
  !running &&
  !hasNotice &&
  !conditionsMenuOpen;

export type AiGenerateDockOnboardingRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/** 강조 구멍 — 대상 상자를 단계별 여백만큼 넓히거나 줄인다. */
export const toAiGenerateDockOnboardingHole = (
  target: AiGenerateDockOnboardingRect,
  step: AiGenerateDockOnboardingStep,
): AiGenerateDockOnboardingRect => {
  const outset = AI_GENERATE_DOCK_ONBOARDING_HOLE_OUTSET[step];
  return {
    top: target.top - outset.top,
    left: target.left - outset.left,
    width: target.width + outset.left + outset.right,
    height: target.height + outset.top + outset.bottom,
  };
};

/** 카드가 붙는 기준 상자 — 연결선 끝 점이 구멍 위 가장자리 안쪽에 닿도록 구멍을 그만큼 내린다. */
export const toAiGenerateDockOnboardingAnchor = (
  hole: AiGenerateDockOnboardingRect,
): AiGenerateDockOnboardingRect => ({
  ...hole,
  top: hole.top + AI_GENERATE_DOCK_ONBOARDING_CONNECTOR_OVERLAP_PX,
  height: Math.max(
    hole.height - AI_GENERATE_DOCK_ONBOARDING_CONNECTOR_OVERLAP_PX,
    0,
  ),
});
